import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { database, auth } from '../firebaseConfig';
import { ref, onValue, push, set } from 'firebase/database';
import { getTransactions } from '../services/transactionService';

const FONT = 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const GOLD = '#D4AF37';
const STEP_COUNT = 6; // address, day, times, notes, coverage, review

// Local calendar date as YYYY-MM-DD so it matches the stored value.
function toDateKey(d) {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

// Upcoming weekend: this Saturday/Sunday, or next weekend once the current one has passed.
function upcomingWeekend(now = new Date()) {
  const base = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const daysToSat = (6 - base.getDay() + 7) % 7;
  const sat = new Date(base);
  sat.setDate(base.getDate() + daysToSat);
  const sun = new Date(sat);
  sun.setDate(sat.getDate() + 1);
  return [
    { key: toDateKey(sat), date: sat },
    { key: toDateKey(sun), date: sun },
  ];
}

function prettyDate(d) {
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

function labelForKey(weekend, key) {
  const match = weekend.find((w) => w.key === key);
  return match ? prettyDate(match.date) : key;
}

const emptyForm = () => ({ listingId: '', customAddress: '', date: '', startTime: '', endTime: '', notes: '', needsCoverage: null });

const styles = {
  page: { minHeight: '100vh', background: '#f5f6f8', padding: '24px 16px', fontFamily: FONT, boxSizing: 'border-box' },
  wrap: { maxWidth: 900, margin: '0 auto' },
  topRow: { display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  h1: { fontSize: 26, fontWeight: 700, color: '#1a1a1a', margin: '8px 0 4px' },
  h2: { fontSize: 19, fontWeight: 700, color: '#1a1a1a', margin: '0 0 6px' },
  muted: { color: '#6b6b6b', fontSize: 14, margin: '0 0 16px', lineHeight: 1.5 },
  card: { background: '#ffffff', border: '1px solid #e3e3e3', borderTop: `3px solid ${GOLD}`, borderRadius: 10, padding: 20, marginBottom: 20 },
  button: { padding: '14px 22px', background: GOLD, color: '#1a1a1a', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 14, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: FONT },
  ghost: { padding: '10px 16px', background: '#ffffff', color: '#1a1a1a', border: '1px solid #cfcfcf', borderRadius: 6, fontSize: 14, cursor: 'pointer', fontFamily: FONT },
  choice: { flex: '1 1 140px', padding: '18px 12px', background: '#ffffff', color: '#1a1a1a', border: '2px solid #cfcfcf', borderRadius: 8, fontSize: 16, fontWeight: 600, cursor: 'pointer', fontFamily: FONT },
  choiceOn: { borderColor: GOLD, background: '#fbf5e0' },
  input: { width: '100%', boxSizing: 'border-box', padding: '12px', border: '1px solid #cfcfcf', borderRadius: 6, fontSize: 16, fontFamily: FONT, color: '#1a1a1a', background: '#ffffff' },
  label: { display: 'block', fontSize: 15, fontWeight: 600, color: '#1a1a1a', marginBottom: 10 },
  hint: { fontSize: 13, color: '#6b6b6b', marginTop: 8, lineHeight: 1.5 },
  item: { border: '1px solid #e3e3e3', borderRadius: 8, padding: 14, marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'space-between', alignItems: 'flex-start' },
  itemTitle: { fontSize: 15, fontWeight: 700, color: '#1a1a1a' },
  itemMeta: { fontSize: 13, color: '#6b6b6b', marginTop: 4, lineHeight: 1.5 },
  badgeNeeded: { display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 999, background: GOLD, color: '#1a1a1a', textTransform: 'uppercase', letterSpacing: '0.05em' },
  badgeCovered: { display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 999, background: '#eef7ee', color: '#2e7d32', border: '1px solid #2e7d32', textTransform: 'uppercase', letterSpacing: '0.05em' },
  empty: { color: '#8a8a8a', fontSize: 14, marginTop: 10 },
  success: { background: '#eef7ee', border: '1px solid #2e7d32', borderRadius: 6, padding: 12, color: '#2e7d32', fontSize: 14, fontWeight: 600, marginBottom: 16 },
  error: { background: '#fbeeee', border: '1px solid #b00020', borderRadius: 6, padding: 12, color: '#b00020', fontSize: 14, marginBottom: 16 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 100, boxSizing: 'border-box' },
  modal: { background: '#ffffff', borderRadius: 12, borderTop: `4px solid ${GOLD}`, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', padding: 24, boxSizing: 'border-box', fontFamily: FONT },
  stepCount: { fontSize: 12, color: '#8a8a8a', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 },
  actions: { display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 24 },
  summaryRow: { display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderBottom: '1px solid #efefef', fontSize: 14 },
};

export default function OpenHousesPage() {
  const { user } = useAuth();
  const weekend = useMemo(() => upcomingWeekend(), []);
  const weekendKeys = weekend.map((w) => w.key);

  const [openHouses, setOpenHouses] = useState([]);
  const [listings, setListings] = useState([]);
  const [listingsLoaded, setListingsLoaded] = useState(false);
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm());
  const [stepError, setStepError] = useState('');

  const agentName = user?.displayName || user?.email || 'Agent';
  const myUid = user?.uid || '';

  useEffect(() => {
    const unsub = onValue(
      ref(database, 'openHouses'),
      (snap) => {
        const val = snap.val() || {};
        setOpenHouses(Object.entries(val).map(([id, v]) => ({ id, ...v })));
      },
      (err) => console.error('Could not load open houses', err)
    );
    return () => unsub();
  }, []);

  // The agent's own active listings (live or active) for the listing dropdown.
  useEffect(() => {
    if (!myUid) return;
    let cancelled = false;
    getTransactions(myUid)
      .then((txs) => {
        if (cancelled) return;
        setListings(txs.filter((t) => ['live', 'listing-active'].includes(t.status) && t.propertyAddress));
        setListingsLoaded(true);
      })
      .catch((err) => {
        console.error('Could not load your listings', err);
        if (!cancelled) setListingsLoaded(true);
      });
    return () => { cancelled = true; };
  }, [myUid]);

  const weekendOpenHouses = openHouses
    .filter((o) => weekendKeys.includes(o.date))
    .sort((a, b) => (a.date + (a.startTime || '')).localeCompare(b.date + (b.startTime || '')));

  const showMessage = (type, text) => setMessage({ type, text });

  const openWizard = () => {
    setForm(emptyForm());
    setStep(0);
    setStepError('');
    setWizardOpen(true);
  };

  const closeWizard = () => {
    setWizardOpen(false);
    setStepError('');
  };

  const selectedAddress = () => {
    if (form.listingId === 'other') return form.customAddress.trim();
    const match = listings.find((l) => l.id === form.listingId);
    return (match?.propertyAddress || '').trim();
  };

  const validateStep = (i) => {
    if (i === 0) {
      if (!form.listingId) return 'Choose a listing from the list.';
      if (form.listingId === 'other' && !form.customAddress.trim()) return 'Enter the property address.';
    }
    if (i === 1 && !form.date) return 'Choose Saturday or Sunday.';
    if (i === 2) {
      if (!form.startTime || !form.endTime) return 'Enter a start and end time.';
      if (form.endTime <= form.startTime) return 'The end time must be after the start time.';
    }
    if (i === 4 && form.needsCoverage === null) return 'Choose Yes or No.';
    return '';
  };

  const nextStep = (e) => {
    if (e) e.preventDefault();
    const err = validateStep(step);
    if (err) return setStepError(err);
    setStepError('');
    if (step < STEP_COUNT - 1) setStep(step + 1);
  };

  const backStep = () => {
    setStepError('');
    if (step > 0) setStep(step - 1);
  };

  const submitOpenHouse = async () => {
    if (!myUid) return;
    setSaving(true);
    try {
      await set(push(ref(database, 'openHouses')), {
        agentUid: myUid,
        agentName,
        address: selectedAddress(),
        listingId: form.listingId === 'other' ? '' : form.listingId,
        date: form.date,
        startTime: form.startTime,
        endTime: form.endTime,
        notes: form.notes.trim(),
        needsCoverage: form.needsCoverage === true,
        coverageStatus: form.needsCoverage === true ? 'needed' : 'none',
        createdAt: Date.now(),
      });
      setWizardOpen(false);
      showMessage(
        'success',
        form.needsCoverage === true
          ? 'Your open house is registered and shows as needing coverage.'
          : 'Your open house is registered on the weekend list.'
      );
    } catch (err) {
      console.error('Could not register open house', err);
      setStepError('Could not save the open house. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const onWizardSubmit = (e) => {
    e.preventDefault();
    if (step === STEP_COUNT - 1) return submitOpenHouse();
    return nextStep();
  };

  const removeOpenHouse = async (item) => {
    if (!window.confirm('Remove this open house from the weekend list?')) return;
    try {
      await set(ref(database, `openHouses/${item.id}`), null);
    } catch (err) {
      console.error('Could not remove open house', err);
      showMessage('error', 'Could not remove that open house. Please try again.');
    }
  };

  // Claim / release go through the server: one claimer per open house (atomic), and the confirmation email
  // and texts are sent there once per claim. Same rules for the AI connection.
  const callClaim = async (item, action) => {
    const idToken = await auth.currentUser.getIdToken();
    const res = await fetch('/api/open-house-claim', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` }, body: JSON.stringify({ id: item.id, action }) });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || '');
    return data;
  };
  const claimCoverage = async (item) => {
    if (item.coverageStatus !== 'needed') return;
    try {
      await callClaim(item, 'claim');
      showMessage('success', `You are covering ${item.address}. Thank you! A confirmation email and text are on the way.`);
    } catch (err) {
      console.error('Could not claim coverage', err);
      showMessage('error', err.message || 'Could not claim that open house. Please try again.');
    }
  };

  const unclaimCoverage = async (item) => {
    if (item.coverageStatus !== 'covered' || item.claimedByUid !== myUid) return;
    if (!window.confirm(`Release ${item.address}? It will show as needing coverage again.`)) return;
    try {
      await callClaim(item, 'unclaim');
      showMessage('success', `You released ${item.address}. It now needs coverage again.`);
    } catch (err) {
      console.error('Could not unclaim coverage', err);
      showMessage('error', err.message || 'Could not release that open house. Please try again.');
    }
  };


  const renderStep = () => {
    if (step === 0) {
      return (
        <>
          <label style={styles.label} htmlFor="oh-listing">Which listing is this open house for?</label>
          <select id="oh-listing" style={styles.input} value={form.listingId} onChange={(e) => { setForm({ ...form, listingId: e.target.value }); setStepError(''); }}>
            <option value="">{listingsLoaded ? 'Choose a listing' : 'Loading your listings...'}</option>
            {listings.map((l) => (
              <option key={l.id} value={l.id}>{l.propertyAddress}</option>
            ))}
            <option value="other">Enter address myself</option>
          </select>
          {listingsLoaded && !listings.length && (
            <p style={styles.hint}>You have no active listings yet. Choose Enter address myself to type it in.</p>
          )}
          {form.listingId === 'other' && (
            <div style={{ marginTop: 14 }}>
              <label style={{ ...styles.hint, marginTop: 0 }} htmlFor="oh-address">Property address</label>
              <input id="oh-address" autoFocus style={styles.input} value={form.customAddress} onChange={(e) => setForm({ ...form, customAddress: e.target.value })} placeholder="e.g., 123 Example Dr, Estero" />
            </div>
          )}
        </>
      );
    }
    if (step === 1) {
      return (
        <>
          <span style={styles.label}>Which day is the open house?</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {weekend.map((w) => (
              <button
                key={w.key}
                type="button"
                style={{ ...styles.choice, ...(form.date === w.key ? styles.choiceOn : {}) }}
                onClick={() => { setForm({ ...form, date: w.key }); setStepError(''); }}
              >
                {w.date.toLocaleDateString('en-US', { weekday: 'long' })}
                <div style={{ fontSize: 13, fontWeight: 400, color: '#6b6b6b', marginTop: 4 }}>
                  {w.date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
                </div>
              </button>
            ))}
          </div>
        </>
      );
    }
    if (step === 2) {
      return (
        <>
          <span style={styles.label}>What are the open house hours?</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ flex: '1 1 160px' }}>
              <label style={{ ...styles.hint, marginTop: 0 }} htmlFor="oh-start">Start time</label>
              <input id="oh-start" type="time" style={styles.input} value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} />
            </div>
            <div style={{ flex: '1 1 160px' }}>
              <label style={{ ...styles.hint, marginTop: 0 }} htmlFor="oh-end">End time</label>
              <input id="oh-end" type="time" style={styles.input} value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} />
            </div>
          </div>
        </>
      );
    }
    if (step === 3) {
      return (
        <>
          <label style={styles.label} htmlFor="oh-notes">Any notes for the team? (optional)</label>
          <textarea id="oh-notes" rows={4} style={{ ...styles.input, resize: 'vertical' }} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Parking, refreshments, lockbox details, what to bring" />
        </>
      );
    }
    if (step === 4) {
      return (
        <>
          <span style={styles.label}>Do you need someone to cover this open house?</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            <button type="button" style={{ ...styles.choice, ...(form.needsCoverage === true ? styles.choiceOn : {}) }} onClick={() => { setForm({ ...form, needsCoverage: true }); setStepError(''); }}>Yes, I need coverage</button>
            <button type="button" style={{ ...styles.choice, ...(form.needsCoverage === false ? styles.choiceOn : {}) }} onClick={() => { setForm({ ...form, needsCoverage: false }); setStepError(''); }}>No, I will host</button>
          </div>
          {form.needsCoverage === true && (
            <p style={styles.hint}>This will show as needing coverage so other agents can claim it.</p>
          )}
        </>
      );
    }
    return (
      <>
        <span style={styles.label}>Review and register</span>
        <div>
          <div style={styles.summaryRow}><span>Address</span><strong>{selectedAddress()}</strong></div>
          <div style={styles.summaryRow}><span>Day</span><strong>{labelForKey(weekend, form.date)}</strong></div>
          <div style={styles.summaryRow}><span>Hours</span><strong>{form.startTime} to {form.endTime}</strong></div>
          <div style={styles.summaryRow}><span>Notes</span><strong>{form.notes.trim() || "None"}</strong></div>
          <div style={{ ...styles.summaryRow, borderBottom: 'none' }}><span>Coverage</span><strong>{form.needsCoverage ? 'Needs coverage' : 'Agent is hosting'}</strong></div>
        </div>
      </>
    );
  };

  return (
    <div style={styles.page}>
      <div style={styles.wrap}>
        <div style={styles.topRow}>
          <div>
            <h1 style={styles.h1}>Open Houses</h1>
            <p style={{ ...styles.muted, marginBottom: 0 }}>
              Weekend of {prettyDate(weekend[0].date)} and {prettyDate(weekend[1].date)}.
            </p>
          </div>
          <button type="button" style={styles.button} onClick={openWizard}>Register New Open House</button>
        </div>

        {message && <div style={{ ...(message.type === 'success' ? styles.success : styles.error), marginTop: 16 }}>{message.text}</div>}

        <div style={{ ...styles.card, marginTop: 20 }}>
          <h2 style={styles.h2}>Open houses this weekend</h2>
          <p style={styles.muted}>Open houses that need coverage are marked. Any agent can claim one.</p>
          {!weekendOpenHouses.length && <p style={styles.empty}>No open houses registered yet for this weekend.</p>}
          {weekendOpenHouses.map((o) => {
            const mine = o.agentUid === myUid;
            return (
              <div key={o.id} style={styles.item}>
                <div style={{ minWidth: 0 }}>
                  <div style={styles.itemTitle}>{o.address}</div>
                  <div style={styles.itemMeta}>
                    {labelForKey(weekend, o.date)}
                    {o.startTime ? `, ${o.startTime}` : ''}{o.endTime ? ` to ${o.endTime}` : ''}
                  </div>
                  <div style={styles.itemMeta}>Agent: {o.agentName}</div>
                  {o.coverageStatus === 'covered' && <div style={styles.itemMeta}>Covered by: {o.claimedByName}</div>}
                  {o.notes && <div style={styles.itemMeta}>{o.notes}</div>}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
                  {o.coverageStatus === 'needed' && <span style={styles.badgeNeeded}>Needs coverage</span>}
                  {o.coverageStatus === 'covered' && <span style={styles.badgeCovered}>Covered</span>}
                  {o.coverageStatus === 'needed' && !mine && (
                    <button type="button" style={styles.button} onClick={() => claimCoverage(o)}>I will host</button>
                  )}
                  {o.coverageStatus === 'covered' && o.claimedByUid === myUid && (
                    <button type="button" style={styles.ghost} onClick={() => unclaimCoverage(o)}>Unclaim</button>
                  )}
                  {mine && (
                    <button type="button" style={styles.ghost} onClick={() => removeOpenHouse(o)}>Remove</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {wizardOpen && (
        <div style={styles.overlay} role="dialog" aria-modal="true" aria-label="Register new open house">
          <form style={styles.modal} onSubmit={onWizardSubmit}>
            <div style={styles.stepCount}>Step {step + 1} of {STEP_COUNT}</div>
            {renderStep()}
            {stepError && <div style={{ ...styles.error, marginTop: 16, marginBottom: 0 }}>{stepError}</div>}
            <div style={styles.actions}>
              <button type="button" style={styles.ghost} onClick={step === 0 ? closeWizard : backStep}>
                {step === 0 ? 'Cancel' : 'Back'}
              </button>
              <button type="submit" style={styles.button} disabled={saving}>
                {step === STEP_COUNT - 1 ? 'Register open house' : 'Next'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
